import { blue, green, red } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import type { PlotLineOrBand, PointOptionsObject, SeriesWaterfallOptions } from 'highcharts';
import * as i18next from 'i18next';
import Router from 'next/router';

import * as Types from '@/types';

const getDuration = (ms: number, roundUpToNearestMinute = false) => {
  // TODO: Rewrite this to use DateFNS for simplicity?
  const absMs = Math.abs(ms);
  let add = 0;
  let seconds = Math.floor((absMs / (1).seconds) % 60);
  if (roundUpToNearestMinute && seconds >= 1) {
    add = 60000;
    seconds = 0;
  } else if (roundUpToNearestMinute) {
    seconds = 0;
  }
  const minutes = Math.floor(((absMs + add) / (1).minutes) % 60);
  const hours = Math.floor((absMs + add) / (1).hours);
  const zeroHours = hours < 10 ? `0${hours}` : hours;
  const zeroMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const zeroSeconds = seconds < 10 ? `0${seconds}` : seconds;
  const result = {
    hours,
    minutes,
    seconds,
    human: `${ms >= 0 ? '' : '-'}
                      ${zeroHours}:${zeroMinutes}${roundUpToNearestMinute ? '' : `:${zeroSeconds}`}`,
  };
  return result;
};

const getDurationPercentage = (ms: number, totalEquipmentTime: number) => {
  const result = (ms / totalEquipmentTime) * 100;
  return result;
};

const getSeriesData = (
  t: i18next.TFunction,
  oee: Schema.Oee,
  stopCauseStats: Schema.StopCauseStat[],
  theme: Theme,
  displayDeviations: boolean,
): SeriesWaterfallOptions['data'] => {
  const seriesData: Array<PointOptionsObject & { type?: string; stopCategory?: string }> = [];

  if (!oee || !stopCauseStats) {
    return seriesData;
  }

  const aggregatedDeviationTranslation = t(['oee:targetsDeviation'], {
    defaultValue: 'Targets deviation',
  });

  seriesData.push({
    name: t(['oee:totalEquipmentTime'], { defaultValue: 'Total Equipment Time' }),
    y: oee.totalEquipmentTime / (1).hours,
  });

  const sortedStopStats = [...stopCauseStats].sort((a, b) => b.accDuration - a.accDuration);

  const noActivityStops = sortedStopStats.filter((s) => s.type === Schema.StopType.NO_ACT);
  const nonProdStops = sortedStopStats.filter((s) => s.type === Schema.StopType.NON_PROD);
  const batchNonProdStops = sortedStopStats.filter((s) => s.type === Schema.StopType.BATCH_NON_PROD);
  const unregisteredStops = sortedStopStats.filter((s) => !s.type && !s.isMicroStop);
  const microStops = sortedStopStats.filter((s) => !s.type && s.isMicroStop);
  const downtimeStops = Array.from(
    sortedStopStats
      .filter((s) => s.type === Schema.StopType.DOWNTIME)
      .reduce<Map<string, Schema.StopCauseStat>>((aggregateDowntimeStops, stop) => {
        const stopCauseName = stop.stopCauseName.trim();
        const s = aggregateDowntimeStops.get(stopCauseName);
        if (!s) {
          aggregateDowntimeStops.set(stopCauseName, { ...stop });
        } else {
          s.accDuration += stop.accDuration;
        }

        return aggregateDowntimeStops;
      }, new Map())
      .values(),
  );

  {
    const aggregateDeviations = displayDeviations && noActivityStops.some((stop) => stop.accTarget);
    if (aggregateDeviations) {
      const totalDeviation =
        noActivityStops
          .filter((stop) => stop.accTarget)
          .reduce((acc, stop) => acc + (stop.accTarget - stop.accDuration), 0) / (1).hours;

      const color = 0 < totalDeviation ? green[200] : red[200];

      seriesData.push({
        name: aggregatedDeviationTranslation,
        color,
        y: totalDeviation,
      });
    }

    noActivityStops.forEach((s) => {
      seriesData.push({
        name: s.stopCauseName,
        color: theme.blackbird.stops[s.type!].color.normal,
        y: -((aggregateDeviations && s.accTarget) || s.accDuration) / (1).hours,
        type: s.type!,
        stopCategory: s.stopCategoryName,
      });
    });
  }

  seriesData.push({
    name: t(['oee:mannedTime'], { defaultValue: 'Manned Time' }),
    isSum: true,
  });

  {
    const aggregateDeviations = displayDeviations && nonProdStops.some((stop) => stop.accTarget);
    if (aggregateDeviations) {
      const totalDeviation =
        nonProdStops
          .filter((stop) => stop.accTarget)
          .reduce((acc, stop) => acc + (stop.accTarget - stop.accDuration), 0) / (1).hours;
      const color = 0 < totalDeviation ? green[200] : red[200];

      seriesData.push({
        name: aggregatedDeviationTranslation,
        color,
        y: totalDeviation,
      });
    }

    nonProdStops.forEach((s) => {
      seriesData.push({
        name: s.stopCauseName,
        color: theme.blackbird.stops[s.type!].color.normal,
        y: -((aggregateDeviations && s.accTarget) || s.accDuration) / (1).hours,
        type: s.type!,
        stopCategory: s.stopCategoryName,
      });
    });
  }

  seriesData.push({
    name: t(['oee:productionTime'], { defaultValue: 'Production Time' }),
    isSum: true,
  });

  {
    const aggregateDeviations = displayDeviations && batchNonProdStops.some((stop) => stop.accTarget);
    if (aggregateDeviations) {
      const totalDeviation =
        batchNonProdStops
          .filter((stop) => stop.accTarget)
          .reduce((acc, stop) => acc + (stop.accTarget - stop.accDuration), 0) / (1).hours;
      const color = 0 < totalDeviation ? green[200] : red[200];

      seriesData.push({
        name: aggregatedDeviationTranslation,
        color,
        y: totalDeviation,
      });
    }

    batchNonProdStops.forEach((s) => {
      seriesData.push({
        name: s.stopCauseName,
        color: theme.blackbird.stops[s.type!].color.normal,
        y: -((aggregateDeviations && s.accTarget) || s.accDuration) / (1).hours,
        type: s.type!,
        stopCategory: s.stopCategoryName,
      });
    });
  }

  seriesData.push({
    name: t(['oee:operatingTime'], { defaultValue: 'Operating Time' }),
    isSum: true,
  });

  if (oee?.speedLoss) {
    seriesData.push({
      name: t(['oee:speedLoss'], { defaultValue: 'Speed loss' }),
      color: theme.blackbird.stops.MICRO_STOP.color.normal,
      y: -(oee.speedLoss / (1).hours),
    });
  }

  if (oee?.scrapLoss) {
    seriesData.push({
      name: t(['oee:scrapLoss'], { defaultValue: 'Scrap loss' }),
      color: theme.blackbird.stops.MICRO_STOP.color.normal,
      y: -(oee.scrapLoss / (1).hours),
    });
  }

  if (unregisteredStops.length) {
    const totalMs = unregisteredStops.reduce((acc, stat) => acc + stat.accDuration, 0);
    seriesData.push({
      name: t(['shared:unregisteredStops'], { defaultValue: 'Unregistered stops' }),
      color: theme.blackbird.stops.UNREGISTERED.color.normal,
      y: -(totalMs / (1).hours),
      type: 'UNREGISTERED',
    });
  }

  if (microStops.length) {
    const totalMs = microStops.reduce((acc, stat) => acc + stat.accDuration, 0);
    seriesData.push({
      name: t(['shared:microStops'], { defaultValue: 'Micro stops' }),
      color: theme.blackbird.stops.MICRO_STOP.color.normal,
      y: -(totalMs / (1).hours),
      type: 'MICRO_STOP',
    });
  }

  {
    const aggregateDeviations = displayDeviations && downtimeStops.some((stop) => stop.accTarget);
    if (aggregateDeviations) {
      const totalDeviation =
        downtimeStops
          .filter((stop) => stop.accTarget)
          .reduce((acc, stop) => acc + (stop.accTarget - stop.accDuration), 0) / (1).hours;
      const color = 0 < totalDeviation ? green[200] : red[200];

      seriesData.push({
        name: aggregatedDeviationTranslation,
        color,
        y: totalDeviation,
      });
    }

    downtimeStops.forEach((s) => {
      seriesData.push({
        name: s.stopCauseName,
        color: theme.blackbird.stops[s.type!].color.normal,
        y: -((aggregateDeviations && s.accTarget) || s.accDuration) / (1).hours,
        type: s.type!,
        stopCategory: s.stopCategoryName,
      });
    });
  }

  seriesData.push({
    name: t(['oee:valuedOperatingTime'], { defaultValue: 'Valued Operating Time' }),
    isSum: true,
    color: 'rgba(0,153,76,0.5)',
  });

  // Handle multiple stopCauses with the same name in different categories, by
  // appending spaces behind the name, to get individual bars in waterfall.
  return seriesData.reduce((acc, cur) => {
    let name = cur.name;
    while (acc.find((s) => s.name === name)) {
      name = `${name} `;
    }
    acc.push({
      ...cur,
      name,
    });
    return acc;
  }, [] as PointOptionsObject[]);
};

const getPlotbands = (
  stopStats: Schema.StopCauseStat[],
  oee: Schema.Oee,
  theme: Theme,
  displayDeviations: boolean,
): PlotLineOrBand[] => {
  const setAlphaOfColor = (rgbColor: string, alpha: number) => {
    return rgbColor.replace(/[\d\.]+\)$/g, `${alpha})`); // eslint-disable-line no-useless-escape
  };

  const noActStops = stopStats.filter((s) => s.type === Schema.StopType.NO_ACT);
  const nonProdStops = stopStats.filter((s) => s.type === Schema.StopType.NON_PROD);
  const batchNonProdStops = stopStats.filter((s) => s.type === Schema.StopType.BATCH_NON_PROD);

  const downtimeStops = stopStats
    .filter((s) => s.type === Schema.StopType.DOWNTIME)
    .map(({ accDuration }) => ({ accDuration }));

  const unregisteredStops = stopStats.filter((s) => !s.type && !s.isMicroStop);
  const microStops = stopStats.filter((s) => !s.type && s.isMicroStop);

  if (microStops.length) {
    downtimeStops.push({
      accDuration: microStops.reduce((acc, curr) => acc + curr.accDuration, 0),
    });
  }
  if (unregisteredStops.length) {
    downtimeStops.push({
      accDuration: unregisteredStops.reduce((acc, curr) => acc + curr.accDuration, 0),
    });
  }
  if (oee && oee.speedLoss) {
    downtimeStops.push({
      accDuration: oee.speedLoss,
    });
  }
  if (oee && oee.scrapLoss) {
    downtimeStops.push({
      accDuration: oee.scrapLoss,
    });
  }

  const typeStopsPairs: Array<[Schema.StopType, Array<{ accDuration: number; accTarget?: number | null }>]> = [
    [Schema.StopType.NO_ACT, noActStops],
    [Schema.StopType.NON_PROD, nonProdStops],
    [Schema.StopType.BATCH_NON_PROD, batchNonProdStops],
    [Schema.StopType.DOWNTIME, downtimeStops],
  ];

  const [, plotBands] = typeStopsPairs.reduce<[number, PlotLineOrBand[]]>(
    ([offset, bands], [type, stops], i) => {
      const extraColumn = +(displayDeviations && stops.some((stop) => !!stop.accTarget));
      const width = stops.length + extraColumn;

      bands.push({
        color: setAlphaOfColor(
          stops.length ? theme.blackbird.stops[type].color.normal : 'white',
          stops.length ? 0.7 : 0,
        ),
        from: offset,
        to: width + offset,
        label: {
          // @ts-ignore
          text: `${(stops.reduce((acc, cur) => acc + cur.accDuration, 0) / (1).hours).toFixed(0)}h`,
          style: {
            color: 'black',
            fontWeight: 'bold',
          },
        },
      });

      return [offset + width + 1, bands];
    },
    [0.5, []],
  );

  return plotBands;
};

interface GraphConfig {
  height: string;
  refetch: Function;
  timeRange: Required<Schema.Time>;
  stopStats: Schema.StopCauseStat[];
  oee: Schema.Oee;
  theme: Theme;
  t: i18next.TFunction;
  router: typeof Router;
  displayDeviations: boolean;
  toggleDisplayDeviations: () => void;
}

const getConfig = ({
  height,
  stopStats,
  oee,
  theme,
  t,
  router,
  displayDeviations,
  toggleDisplayDeviations,
}: GraphConfig) => {
  const anyTargets = stopStats.some((stop) => !!stop.accTarget);

  return {
    chart: {
      type: 'waterfall',
      zoomType: 'x',
      height,
      animation: {
        duration: 1000,
      },
    },
    exporting: {
      enabled: true,
      filename: 'oee-chart',
      sourceWidth: 2048,
      sourceHeight: 512,
      scale: 1,
      chartOptions: {
        title: false,
      },
      menuItemDefinitions: {
        printChart: {
          text: t(['shared:printChart'], { defaultValue: 'Print chart' }),
        },
        downloadPNG: {
          text: t(['shared:downloadPNG'], { defaultValue: 'Download PNG image' }),
        },
        downloadJPEG: {
          text: t(['shared:downloadJPEG'], { defaultValue: 'Download JPEG image' }),
        },
        downloadPDF: {
          text: t(['shared:downloadPDF'], { defaultValue: 'Download PDF image' }),
        },
        downloadSVG: {
          text: t(['shared:downloadSVG'], { defaultValue: 'Download SVG image' }),
        },
      },
      buttons: {
        contextButton: {
          y: 0,
          x: 0,
          menuItems: ['printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downdloadSVG'],
        },
      },
    },
    title: false,
    xAxis: {
      type: 'category',
      plotBands: getPlotbands(stopStats, oee, theme, displayDeviations),
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          verticalAlign: 'top',
          x: 0,
          y: -25,
          style: {
            color: 'black',
          },
        },
        point: {
          events: {
            mouseOver(e: Types.HighchartsOptions) {
              if (e.target?.type) {
                e.target.graphic.element.style.cursor = 'pointer';
              }
            },
          },
        },
      },
    },

    yAxis: {
      title: {
        text: t(['shared:hours'], { defaultValue: 'Hours' }),
      },
      labels: {
        overflow: 'justify',
      },
    },

    legend: {
      enabled: anyTargets,
    },

    tooltip: {
      pointFormat: `<b>{point.y:,.1f}</b> ${t(['shared:hours'], { defaultValue: 'Hours' })}`,
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        showInLegend: false,
        upColor: 'rgba(70,130,180,0.7)',
        tooltip: {
          pointFormatter(this: any) {
            const htmlString = `<b>${this.name}:</b> ${this.y < 0 ? '-' : ''}${
              getDuration(Math.abs(this.y * (1).hours)).human
            }<br/>
              <b>Percent : </b> ${getDurationPercentage(Math.abs(this.y * (1).hours), oee.totalEquipmentTime).toFixed(
                1,
              )} %`;

            if (this.stopCategory) {
              return `<b>${this.stopCategory}</b><br/>${htmlString}`;
            } else {
              return htmlString;
            }
          },
        },
        minPointLength: 5,
        borderColor: 'rgba(0,0,0,0)',
        lineColor: '#ccc',
        data: getSeriesData(t, oee, stopStats, theme, displayDeviations),
        dataLabels: {
          enabled: true,
          defer: false,
          formatter(this: any) {
            if (Math.abs(this.y) > 10) {
              return `${this.y.toFixed(0)}h`;
            } else {
              return `${this.y.toFixed(1)}h`;
            }
          },

          style: {
            fontWeight: 'bold',
          },
        },
        pointPadding: 0,
        events: {
          click(e: { point: Types.HighchartsPoint }) {
            if (e.point.type && e.point.name) {
              const { pathname, query } = router;
              Router.push({
                pathname,
                query: { ...query, tab: 'analytics', name: e.point.name, type: e.point.type },
              });
            }
          },
        },
      },
      {
        showInLegend: true,
        visible: displayDeviations && anyTargets,
        color: blue[200],
        name: t(['oee:aggregateTargetDeviations'], {
          defaultValue: 'Aggregate target deviations',
        }),
        events: {
          legendItemClick(this: any, e: Event) {
            e.preventDefault();
            toggleDisplayDeviations();
          },
        },
      },
    ],
  };
};

export default getConfig;
